import { useEffect, useRef, useState } from "react";
import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useTransform,
  type MotionValue,
} from "framer-motion";

/**
 * The ffffffffriends-style card wheel: one card holds center focus while its
 * neighbors sit further out on a tilted disc — blurred, dimmed, scaled down.
 * A rAF-driven progress value turns the disc continuously; it slows through
 * each center card (the "step" beat) but never halts. Only the centered card
 * plays its clip, and it rewinds only once it's out of sight round the back,
 * so every pass shows the moment from the top.
 *
 * Built for the story lander (`landing_story`) and lifted here so the
 * onboarding flow's reveal screen turns the same wheel rather than a second
 * copy of it. Both surfaces render identical geometry; they differ only in
 * how much vertical room they can spare (`chromeReservePx`).
 */

// Card width and the gap between card centers. Spacing > width so cards have
// clear air between them.
const CARD_W = { base: 168, md: 176 };
const SPACING_FACTOR = 1.6;
// Angular step between adjacent cards on the disc (smaller = larger circle).
const ARC_DEG = 10;
// Phase offset in slots: the disc's apex sits this many slots from center.
// -1 puts the apex on the RIGHT neighbor, so cards ascend left → right.
const ARC_PHASE = -1;
// Wrapper height as a multiple of card height (card + arc headroom).
const CARD_BOX_RATIO = 1.28;

/**
 * How near the centre a card must be to carry a video rather than its still.
 *
 * The clips cost far less than they seemed to. What was actually stalling an
 * older iPhone was the rotation itself — `z-index` written every frame, a
 * pointless 3D context, and cards WebKit had no reason to cache. With that
 * fixed the wheel stayed smooth on one clip, so this opened back up to the
 * centre card and its immediate neighbours: two or three at a time, never the
 * whole deck. Eight decoders was always the wrong number; three is the part
 * of the wheel anyone is actually reading.
 *
 * The lead time also matters — a card enters this window well before it
 * reaches the middle, so the clip has buffered and reached a first frame by
 * the time it is centre stage. Until then its own still is the poster, so the
 * swap is invisible.
 */
const VIDEO_SLOTS = 1.2;

/** True for the cards close enough to the middle to be worth a decoder. */
function ownsVideo(offset: number): boolean {
  return Math.abs(offset) < VIDEO_SLOTS;
}

/**
 * Blur by ring, applied from the same state that drives stacking order — so
 * it is written when a card crosses into the next slot, not on every frame.
 *
 * As a per-frame `useTransform` this was one of the things that made the wheel
 * unusable on an older phone: a blurred layer cannot be composited unchanged,
 * so WebKit re-blurred eight textures sixty times a second for a difference no
 * one can see between frames. Quantised, each layer is blurred once and cached
 * until the card moves a whole slot.
 */
const BLUR_BY_RING = ["none", "blur(3px)", "blur(6px)", "blur(9px)"];

// Advance cadence. Step durations are deliberately uneven (base × factor,
// cycling) so the rhythm feels organic rather than metronomic.
const STEP_BASE_MS = 2200;
const STEP_FACTORS = [1, 0.7, 1.3, 0.85, 1.2, 0.75, 1.1];
const CYCLE_MS = STEP_FACTORS.reduce((a, b) => a + b, 0) * STEP_BASE_MS;

// Within-step easing with a HIGH floor velocity: 55% linear + 45%
// smootherstep. The wheel turns at a steady, clearly visible pace — 0.55×
// through each center card, ~1.4× between — so the beats read as gentle
// breathing, never a stop. Entry/exit velocities match across steps.
function stepEase(x: number): number {
  const s = x * x * x * (x * (x * 6 - 15) + 10);
  return 0.55 * x + 0.45 * s;
}

export interface StoryShot {
  img: string;
  /** Optional looping clip; the still doubles as poster + reduced-motion fallback. */
  video?: string;
}

/**
 * Card width that respects the viewport in both axes: the base size by
 * breakpoint, capped to whatever height is left after the rest of the screen
 * has taken its share. Keeps the wheel inside the phone on short devices.
 */
function useCardWidth(chromeReservePx: number, maxCardW?: number): number {
  const measure = () => {
    const byBreakpoint = window.innerWidth >= 768 ? CARD_W.md : CARD_W.base;
    const spare = window.innerHeight - chromeReservePx;
    const byHeight = Math.round(((spare / CARD_BOX_RATIO) * 9) / 16);
    return Math.max(96, Math.min(maxCardW ?? byBreakpoint, byBreakpoint, byHeight));
  };
  const [cardW, setCardW] = useState(measure);
  useEffect(() => {
    const onResize = () => setCardW(measure());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chromeReservePx, maxCardW]);
  return cardW;
}

function WheelCard({
  shot,
  index,
  n,
  progress,
  spacing,
  cardW,
  cardH,
}: {
  shot: StoryShot;
  index: number;
  n: number;
  progress: MotionValue<number>;
  spacing: number;
  cardW: number;
  cardH: number;
}) {
  const reducedMotion = useReducedMotion();
  // Signed shortest circular distance from the (fractional) active position.
  // The sign flip at ±n/2 happens at opacity 0, so the wrap is invisible.
  const offset = useTransform(progress, (p) => {
    let o = (((index - p) % n) + n) % n;
    if (o > n / 2) o -= n;
    return o;
  });
  // Disc geometry: cards sit on a circle whose center is far below the strip,
  // as if mounted on a slowly turning wheel, tilted so its apex sits at the
  // right-neighbor slot (ARC_PHASE) — the right card rides higher than the
  // middle, the left card lower. Each card tilts along the circle's tangent.
  // The radius is derived from `spacing` so adjacent cards keep their gap.
  const arcRad = (ARC_DEG * Math.PI) / 180;
  const phase = ARC_PHASE * arcRad;
  const radius = spacing / Math.sin(arcRad);
  const x = useTransform(
    offset,
    (o) => radius * (Math.sin(o * arcRad + phase) - Math.sin(phase))
  );
  const y = useTransform(
    offset,
    (o) => radius * (Math.cos(phase) - Math.cos(o * arcRad + phase))
  );
  const rotateZ = useTransform(offset, (o) => (o + ARC_PHASE) * ARC_DEG);
  // Falloff is gentle because the blur carries most of the depth cue; without
  // it these curves had to be steeper to keep the middle card distinct.
  const scale = useTransform(offset, (o) => 1 - Math.min(Math.abs(o), 3) * 0.12);
  const opacity = useTransform(offset, (o) => Math.max(0, 1 - Math.abs(o) * 0.3));

  // Stacking order is React state, not a motion value.
  //
  // It was `useTransform`, so framer wrote `z-index` on all eight cards on
  // every frame. `z-index` is not a compositor property: touching it makes
  // WebKit re-sort the stacking context and re-paint what it holds — here,
  // eight rounded cards each carrying a 25px drop shadow. That is a repaint
  // storm sixty times a second for a value that only changes when a card
  // crosses into the next slot, which is a few times per revolution.
  const [depth, setDepth] = useState(() => Math.round(Math.abs(offset.get())));
  const depthRef = useRef(depth);

  // Whether this is the card holding the video element. Same treatment, same
  // reason: a boundary crossing, not a per-frame value.
  const [hasVideo, setHasVideo] = useState(() => ownsVideo(offset.get()));
  const hasVideoRef = useRef(hasVideo);
  // The clip is transparent until it can actually paint a frame. The still
  // underneath is what shows until then — see the stacked markup below.
  const [clipReady, setClipReady] = useState(false);

  // The clip plays through once while its card holds centre focus and holds
  // its last frame if it finishes early. Nothing rewinds: the element mounts
  // fresh each time the card comes round, so every pass starts from the top.
  // (muted is set imperatively because React sets the property but not the
  // attribute, and the autoplay policy checks the attribute.)
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const syncPlayback = (el: HTMLVideoElement | null, o: number) => {
    if (!el) return;
    if (Math.abs(o) < 0.5) {
      if (el.paused && !el.ended) {
        el.muted = true;
        // Older WebViews return undefined instead of a promise from play().
        const started = el.play() as Promise<void> | undefined;
        started?.catch(() => {});
      }
    } else if (!el.paused) {
      el.pause();
    }
  };

  useMotionValueEvent(offset, "change", (o) => {
    syncPlayback(videoRef.current, o);
    const nextDepth = Math.round(Math.abs(o));
    if (nextDepth !== depthRef.current) {
      depthRef.current = nextDepth;
      setDepth(nextDepth);
    }
    const nextHasVideo = ownsVideo(o);
    if (nextHasVideo !== hasVideoRef.current) {
      hasVideoRef.current = nextHasVideo;
      setHasVideo(nextHasVideo);
      // Leaving the window unmounts the element, so the next pass must earn
      // its fade again rather than inheriting the last one's.
      if (!nextHasVideo) setClipReady(false);
    }
  });

  return (
    <motion.div
      className="absolute top-0 left-1/2"
      style={{
        width: cardW,
        height: cardH,
        marginLeft: -cardW / 2,
        x,
        y,
        scale,
        rotateZ,
        opacity,
        zIndex: 10 - depth,
        // Same crossing-driven value as the stacking order: one state change
        // per slot, not one write per frame.
        filter: BLUR_BY_RING[Math.min(depth, BLUR_BY_RING.length - 1)],
        // Everything that moves here is transform and opacity, so each card
        // can be painted once — shadow, rounding and all — and then shunted
        // around as a texture. Without the hint WebKit is free to re-rasterise
        // that shadow every frame, which is what it was doing.
        willChange: "transform, opacity",
        backfaceVisibility: "hidden",
      }}
    >
      {/* Stacked, not swapped. The clip used to replace the still outright, so
          a card went briefly empty while the video element laid out and decoded
          its first frame — that is the flash. The still is now the card, always
          mounted and carrying the shadow and the ring, and the clip lays over
          it and fades up only once it can paint. On the way out the element
          just disappears and the still is already there.

          `object-cover` on both and identical rounding, so the crossfade lands
          on the same framing rather than nudging the image. */}
      <img
        src={shot.img}
        alt=""
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover rounded-[1.75rem] ring-1 ring-white/10 shadow-2xl shadow-black/60"
      />
      {shot.video && hasVideo && !reducedMotion && (
        <video
          ref={(el) => {
            videoRef.current = el;
            syncPlayback(el, offset.get());
          }}
          src={shot.video}
          muted
          playsInline
          // Few enough of these to be worth having ready.
          preload="auto"
          // `canplay` rather than `loadeddata`: the latter fires with a frame
          // decoded but not necessarily presentable, which is exactly the
          // black frame this is here to hide.
          onCanPlay={() => setClipReady(true)}
          className={`absolute inset-0 w-full h-full object-cover rounded-[1.75rem] transition-opacity duration-300 ${
            clipReady ? "opacity-100" : "opacity-0"
          }`}
        />
      )}
    </motion.div>
  );
}

export interface StoryWheelProps {
  shots: StoryShot[];
  /** Pause the wheel when its section isn't on screen. */
  active: boolean;
  /**
   * Vertical space the rest of the screen needs (headline, body, CTA, padding)
   * — what's left over is the wheel's budget. The story lander reserves ~420px
   * for screen 2; a screen with less copy can reserve less.
   */
  chromeReservePx?: number;
  /** Hard cap on card width, for surfaces that want a smaller wheel. */
  maxCardW?: number;
  /**
   * Extra classes on the clipping root. The wheel clips its own overflow, so
   * a surface that pads its content has to widen this or the disc is cut at
   * the padding rather than running off the screen.
   */
  className?: string;
}

export default function StoryWheel({
  shots,
  active,
  chromeReservePx = 420,
  maxCardW,
  className = "",
}: StoryWheelProps) {
  const reducedMotion = useReducedMotion();
  const n = shots.length;
  const cardW = useCardWidth(chromeReservePx, maxCardW);
  const cardH = Math.round((cardW * 16) / 9);
  const spacing = Math.round(cardW * SPACING_FACTOR);

  const progress = useMotionValue(0);
  const elapsedRef = useRef(0);
  useAnimationFrame((_, delta) => {
    if (!active || reducedMotion) return;
    // Clamp delta so a backgrounded tab doesn't fast-forward on return.
    elapsedRef.current += Math.min(delta, 100);
    const cycles = Math.floor(elapsedRef.current / CYCLE_MS);
    let remaining = elapsedRef.current % CYCLE_MS;
    let stepInCycle = 0;
    let frac = 0;
    for (let k = 0; k < STEP_FACTORS.length; k++) {
      const durMs = STEP_FACTORS[k] * STEP_BASE_MS;
      if (remaining < durMs) {
        stepInCycle = k;
        frac = remaining / durMs;
        break;
      }
      remaining -= durMs;
    }
    progress.set(cycles * STEP_FACTORS.length + stepInCycle + stepEase(frac));
  });

  return (
    <div
      // No `w-full`: it pins the width to the parent's content box, so a
      // caller that widens this with negative margins gets a shifted box
      // rather than a wider one — the disc then still clips at the padding.
      // A block-level div fills its container anyway, and the story lander
      // already wraps this in a `w-full` parent.
      // No `perspective`: it used to sit here, but nothing on the disc rotates
      // in 3D — the cards move with x, y, scale and rotateZ, all of them flat.
      // All it did was put every descendant inside a 3D rendering context and
      // change how WebKit decided to composite them.
      className={`relative overflow-hidden pt-6 pb-4 ${className}`}
    >
      {/* Height headroom lets the outer cards dip along the arc uncropped;
          scales with the card so short screens stay inside the viewport. */}
      <div
        className="relative mx-auto"
        style={{ height: Math.round(cardH * CARD_BOX_RATIO) }}
      >
        {shots.map((shot, i) => (
          <WheelCard
            key={shot.img}
            shot={shot}
            index={i}
            n={n}
            progress={progress}
            spacing={spacing}
            cardW={cardW}
            cardH={cardH}
          />
        ))}
      </div>
    </div>
  );
}
